import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/shell/PageHeader';
import ListScreen from '../../components/list/ListScreen';
import useListState from '../../components/list/useListState';
import StatusPill from '../../components/ui/StatusPill';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import { makeCountsGetter, makeListGetter } from '../../actions/listActions';
import { deleteCouponCode } from '../../actions/couponAction';
import { formatDate, number } from '../../helpers/format';
import './growth.css';

const listCoupons = makeListGetter('/coupon/v3/list');
const getCouponCounts = makeCountsGetter('/coupon/v3/list/counts');

const VIEWS = [
  { key: 'all', label: 'All coupons' },
  { key: 'active', label: 'Active', attention: true },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'expired', label: 'Expired' },
];

export default function Coupons() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { confirm, toast } = useOverlays();

  const list = useListState({
    views: VIEWS,
    fetcher: listCoupons,
    countsFetcher: getCouponCounts,
    initialSort: { column: 'redeemed', direction: 'desc' },
  });

  const remove = useCallback(
    async (coupon) => {
      const ok = await confirm({
        title: `Delete ${coupon.code}?`,
        body: `${coupon.code} has been redeemed ${number(coupon.redeemed_count)} times. Deleting it stops it working immediately and cannot be undone.`,
        confirmLabel: 'Delete coupon',
        destructive: true,
      });
      if (!ok) return;
      try {
        await deleteCouponCode(dispatch, coupon.id);
        list.reload();
        list.reloadCounts();
      } catch {
        toast('Could not delete the coupon', { tone: 'error' });
      }
    },
    [confirm, dispatch, toast, list]
  );

  const columns = useMemo(
    () => [
      {
        key: 'code',
        header: 'Code',
        width: 150,
        sortable: true,
        render: (r) => <span className="sv-mono sv-coupon-code">{r.code}</span>,
      },
      {
        key: 'coupon_type',
        header: 'Type',
        width: 120,
        sortable: true,
        render: (r) => (r.coupon_type === 'percentage' ? 'Percentage' : 'Flat'),
      },
      {
        key: 'coupon_value',
        header: 'Value',
        width: 100,
        mono: true,
        render: (r) => (r.coupon_type === 'percentage' ? `${r.coupon_value}%` : `₹${r.coupon_value}`),
      },
      {
        key: 'max_value',
        header: 'Max',
        width: 100,
        mono: true,
        render: (r) => (r.max_value ? `₹${r.max_value}` : '—'),
      },
      {
        key: 'redeemed',
        header: 'Redeemed',
        width: 150,
        sortable: true,
        render: (r) => {
          const pct = r.limit ? Math.min(100, ((r.redeemed_count || 0) / r.limit) * 100) : 0;
          return (
            <div className="sv-redeemed">
              <span className="sv-mono">
                {number(r.redeemed_count)} / {number(r.limit)}
              </span>
              <span className="sv-redeemed__track">
                <span className="sv-redeemed__fill" style={{ width: `${pct}%` }} />
              </span>
            </div>
          );
        },
      },
      {
        key: 'status',
        header: 'Status',
        width: 130,
        render: (r) => <StatusPill status={r.status} />,
      },
      {
        key: 'markets',
        header: 'Markets',
        width: 120,
        muted: true,
        render: (r) => (r.available_countries || []).join(', ') || 'All',
      },
      {
        key: 'ends',
        header: 'Ends',
        width: 120,
        muted: true,
        sortable: true,
        render: (r) => formatDate(r.end_date),
      },
    ],
    []
  );

  return (
    <>
      <PageHeader
        section="Growth"
        title="Coupons"
        subtitle="Discount codes, who can use them, and how far through their limit they are."
        primaryAction={{
          label: 'New coupon',
          icon: 'bx-plus',
          onClick: () => navigate('/coupon-promotion/add'),
        }}
      />

      <ListScreen
        list={list}
        columns={columns}
        rowKey={(r) => r.id}
        noun="coupons"
        selectable={false}
        searchPlaceholder="Coupon code"
        onRowClick={(r) => navigate(`/coupon-promotion/${r.id}`)}
        onRowAction={(r, e) => {
          e.stopPropagation();
          remove(r);
        }}
        empty={{
          icon: 'bx-purchase-tag',
          title: 'No coupons',
          description: 'Create a coupon to start running a promotion.',
          actionLabel: 'New coupon',
          actionIcon: 'bx-plus',
          onAction: () => navigate('/coupon-promotion/add'),
        }}
      />
    </>
  );
}
